import { defineComponent, ref, computed, watch, onMounted, onBeforeUnmount, provide, inject, type InjectionKey, type Ref } from 'vue'
import { useRouter } from 'vue-router'
import {
  Icon as VanIcon,
  Image as VanImage,
  Row as VanRow,
  Col as VanCol,
  List as VanList,
  Empty as VanEmpty,
  Popup as VanPopup,
  NavBar as VanNavBar,
  Field as VanField,
  Uploader as VanUploader,
  Button as VanButton,
  showToast,
} from 'vant'
import {
  getApiV1CommentList,
  getApiV1CommentPopular,
  postApiV1CommentLike,
  postApiV1ContentCollect,
  getApiV1UserLike,
  postApiV1Comment,
} from '@/api'
import type { Comment } from '@/api'
import type { UploaderFileListItem } from 'vant'
import { useAuthStore } from '@/stores/auth-store'
import { useRegionStore } from '@/stores/region-store'
import { uploadImage } from '@/utils/upload'

export const SOCIAL_SCENE = {
  FORUM: 1,
  TUKU: 5,
  ARTICLE: 6,
  BB: 7,
  GUESS: 8,
} as const

export type SocialScene = (typeof SOCIAL_SCENE)[keyof typeof SOCIAL_SCENE]

const SCENE_TO_LIKE_TYPE: Record<SocialScene, number> = {
  [SOCIAL_SCENE.FORUM]: 9,
  [SOCIAL_SCENE.TUKU]: 1,
  [SOCIAL_SCENE.ARTICLE]: 4,
  [SOCIAL_SCENE.BB]: 16,
  [SOCIAL_SCENE.GUESS]: 8,
}

const SCENE_TO_COLLECT_TYPE: Partial<Record<SocialScene, number>> = {
  [SOCIAL_SCENE.TUKU]: 5,
  [SOCIAL_SCENE.BB]: 3,
  [SOCIAL_SCENE.GUESS]: 4,
}

// 评论点赞接口的 object_type 与场景值不完全一致：社区帖评论的点赞用 9 而非 1
const SCENE_TO_COMMENT_LIKE_TYPE: Record<SocialScene, number> = {
  [SOCIAL_SCENE.FORUM]: 9,
  [SOCIAL_SCENE.TUKU]: 5,
  [SOCIAL_SCENE.ARTICLE]: 6,
  [SOCIAL_SCENE.BB]: 7,
  [SOCIAL_SCENE.GUESS]: 8,
}

// 热门评论接口返回的树形结构里 show_child_comments 已递归嵌套整棵子树，
// 客户端一次 flatten 即可平铺到楼中楼列表，避免再调一次 list 接口
function flattenChildren(list: Comment[] | null | undefined): Comment[] {
  const flat: Comment[] = []
  for (const c of list ?? []) {
    flat.push(c)
    if (c.show_child_comments?.length) flat.push(...flattenChildren(c.show_child_comments))
  }
  return flat
}

// ==================== 共享状态 ====================

type ReplyContext = { commentId: number; toUserId: number; toUserName: string }

interface SocialSyncState {
  liked: Ref<boolean>
  collected: Ref<boolean>
  likeCount: Ref<number>
  collectCount: Ref<number>
  likeLoading: Ref<boolean>
  // 由 CommentSection 注入：详情页中段「评论」按钮借此调起底部「评论」按钮同款的弹窗
  commentPopupOpen: Ref<((replyTo?: ReplyContext) => void) | null>
}

const SOCIAL_SYNC_KEY: InjectionKey<SocialSyncState> = Symbol('social-sync')

interface CommentPopupApi {
  open: (replyTo?: ReplyContext) => void
}

const COMMENT_POPUP_KEY: InjectionKey<CommentPopupApi> = Symbol('comment-popup')

export function useSocialSync() {
  const state: SocialSyncState = {
    liked: ref(false),
    collected: ref(false),
    likeCount: ref(0),
    collectCount: ref(0),
    likeLoading: ref(false),
    commentPopupOpen: ref<((replyTo?: ReplyContext) => void) | null>(null),
  }
  provide(SOCIAL_SYNC_KEY, state)
  return state
}

// ==================== 互动操作（CommentSection / SocialStatsRow / SocialBottomBar 共用） ====================

interface SocialTarget {
  scene: SocialScene
  targetId: number | null
  collectTargetId: number | null
}

interface LikeCollectState {
  isLiked: Ref<boolean>
  isCollected: Ref<boolean>
  localLikeCount: Ref<number>
  localCollectCount: Ref<number>
  likeLoading: Ref<boolean>
}

/** 点赞 / 收藏：先改本地状态，接口失败再回滚 */
function useLikeCollect(props: Readonly<SocialTarget>, state: LikeCollectState) {
  const authStore = useAuthStore()
  const router = useRouter()
  const { isLiked, isCollected, localLikeCount, localCollectCount, likeLoading } = state

  async function handleLike() {
    if (!authStore.isLoggedIn) { router.push('/login'); return }
    if (!props.targetId || likeLoading.value) return
    likeLoading.value = true
    const next = !isLiked.value
    isLiked.value = next
    localLikeCount.value += next ? 1 : -1
    try {
      await getApiV1UserLike({ query: { id: props.targetId, type: SCENE_TO_LIKE_TYPE[props.scene] } })
    } catch {
      isLiked.value = !next
      localLikeCount.value += next ? -1 : 1
    } finally {
      likeLoading.value = false
    }
  }

  async function handleCollect() {
    if (!authStore.isLoggedIn) { router.push('/login'); return }
    const cid = props.collectTargetId ?? props.targetId
    if (!cid) return
    const collectType = SCENE_TO_COLLECT_TYPE[props.scene]
    if (!collectType) { showToast('该内容不支持收藏'); return }
    const next = !isCollected.value
    isCollected.value = next
    localCollectCount.value += next ? 1 : -1
    try {
      await postApiV1ContentCollect({ body: { target_type: collectType, target_id: cid, favorite: next ? 1 : 0 } })
    } catch {
      isCollected.value = !next
      localCollectCount.value += next ? -1 : 1
    }
  }

  return { handleLike, handleCollect }
}

/** 点「评论」：优先调起同页面 CommentSection 的弹窗，没有再去 /comment/new */
function useCommentTap(props: Readonly<SocialTarget>, shared: SocialSyncState | null) {
  const authStore = useAuthStore()
  const router = useRouter()
  return function handleCommentTap() {
    if (!authStore.isLoggedIn) { router.push('/login'); return }
    if (!props.targetId) return
    // 优先调起同页面 CommentSection 的弹窗（与底部「评论」按钮同款，避免落到样式混乱的 /comment/new 独立页）
    const open = shared?.commentPopupOpen.value
    if (open) {
      open()
      return
    }
    router.push(`/comment/new?scene=${props.scene}&targetId=${props.targetId}`)
  }
}

interface SocialStateProps extends SocialTarget {
  likeCount: number
  collectCount: number
  initialLiked: boolean
  initialCollected: boolean
}

/**
 * 点赞 / 收藏的本地状态：页面用了 useSocialSync 时与同页组件共用，否则跟随 props 变化。
 * resetOnTarget：切换 targetId 后把本地状态重置回 props（统计行、底部栏需要，评论区不需要）。
 */
function useSocialState(props: Readonly<SocialStateProps>, resetOnTarget: boolean) {
  const shared = inject(SOCIAL_SYNC_KEY, null)
  const state: LikeCollectState = {
    isLiked: shared?.liked ?? ref(props.initialLiked),
    isCollected: shared?.collected ?? ref(props.initialCollected),
    localLikeCount: shared?.likeCount ?? ref(props.likeCount),
    localCollectCount: shared?.collectCount ?? ref(props.collectCount),
    likeLoading: shared?.likeLoading ?? ref(false),
  }
  const { isLiked, isCollected, localLikeCount, localCollectCount } = state

  if (!shared) {
    watch(() => props.likeCount, (v) => (localLikeCount.value = v))
    watch(() => props.collectCount, (v) => (localCollectCount.value = v))
    watch(() => props.initialLiked, (v) => (isLiked.value = v))
    watch(() => props.initialCollected, (v) => (isCollected.value = v))
    if (resetOnTarget) {
      watch(() => props.targetId, () => {
        isLiked.value = props.initialLiked
        isCollected.value = props.initialCollected
        localLikeCount.value = props.likeCount
        localCollectCount.value = props.collectCount
      })
    }
  }

  return { shared, state }
}

/** 分享当前页：支持系统分享就调起，否则复制链接 */
function shareCurrentPage() {
  if (navigator.share) {
    navigator.share({ title: document.title, url: location.href }).catch(() => {})
  } else {
    navigator.clipboard.writeText(location.href).catch(() => {})
    showToast('链接已复制')
  }
}

// ==================== 工具函数 ====================

function formatCount(n: number): string {
  if (n >= 10000) {
    const v = n / 10000
    return v % 1 === 0 ? `${v}万` : `${v.toFixed(1)}万`
  }
  return String(n)
}

function timeAgo(dateStr?: string) {
  if (!dateStr) return ''
  const diff = Date.now() - new Date(dateStr).getTime()
  const m = Math.floor(diff / 60000)
  if (m < 1) return '刚刚'
  if (m < 60) return `${m}分钟前`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}小时前`
  const d = Math.floor(h / 24)
  if (d < 30) return `${d}天前`
  const dt = new Date(dateStr)
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`
}

// ==================== 单条评论 ====================

const CommentItem = defineComponent({
  props: {
    comment: { type: Object as () => Comment, required: true },
    index: { type: Number, default: 0 },
    scene: { type: Number as () => SocialScene, required: true },
    targetId: { type: Number, required: true },
    // 楼主 user_id，命中则显示「LZ」徽标
    authorId: { type: String, default: '' },
    onLike: { type: Function as unknown as () => (id: number, userId: number) => void, required: true },
  },
  setup(props) {
    const router = useRouter()
    const authStore = useAuthStore()
    const textExpanded = ref(false)
    const repliesExpanded = ref(false)
    const childComments = ref<Comment[]>([])
    const loadingReplies = ref(false)

    const TRUNCATE_LEN = 56

    async function loadReplies() {
      if (!props.comment.id) return
      loadingReplies.value = true
      try {
        // 热门评论可能跨 targetId（接口按 lottery_type 聚合），优先用评论自身 object_id 拉子楼
        const objectId = props.comment.object_id ?? props.targetId
        const res = await getApiV1CommentList({
          query: { type: props.scene, id: objectId, parent_id: props.comment.id, page: 1, size: 50 },
        })
        childComments.value = flattenChildren(res.data?.data?.list)
      } finally {
        loadingReplies.value = false
      }
    }

    async function toggleReplies() {
      repliesExpanded.value = !repliesExpanded.value
      if (repliesExpanded.value && childComments.value.length === 0) {
        // 列表/热门接口已在 show_child_comments 里递归嵌套整棵子树，先用预加载数据展开，
        // 避免热门评论再调 list 接口（热评聚合自不同 object，list 拉不到对应子楼）
        const preloaded = flattenChildren(props.comment.show_child_comments)
        if (preloaded.length > 0) {
          childComments.value = preloaded
        } else {
          await loadReplies()
        }
      }
    }

    // 父组件刷新热门/最新列表后，show_child_comments 引用变化；
    // 若当前楼中楼已展开，重新按最新预加载数据 flatten，让新回复立即可见
    watch(
      () => props.comment.show_child_comments,
      (next) => {
        if (repliesExpanded.value) childComments.value = flattenChildren(next)
      },
    )

    const commentPopup = inject(COMMENT_POPUP_KEY, null)
    function handleReply(commentId: number, replyToUser: number, replyToUserName: string) {
      if (!authStore.isLoggedIn) { router.push('/login'); return }
      if (commentPopup) {
        commentPopup.open({ commentId, toUserId: replyToUser, toUserName: replyToUserName })
      } else {
        router.push(
          `/comment/new?scene=${props.scene}&targetId=${props.targetId}&commentId=${commentId}&replyToUser=${replyToUser}&replyToUserName=${encodeURIComponent(replyToUserName)}`,
        )
      }
    }

    return () => {
      const c = props.comment
      const liked = !!c.supported
      const words = c.words ?? ''
      const isLong = words.length > TRUNCATE_LEN
      // 子楼总数：当前已平铺缓存优先，其次用接口给的 remain_child_comment_count
      const totalReplies =
        childComments.value.length > 0
          ? childComments.value.length
          : (c.show_child_comments?.length ?? 0) + (c.remain_child_comment_count ?? 0)
      const isAuthor = !!props.authorId && String(c.user_id ?? '') === props.authorId

      return (
        <div class="comment-item">
          <div class="user-img">
            <div class="left-image">
              <VanImage round src={c.user_pic || ''} width="0.72rem" height="0.72rem" />
              <div class="floor">{isAuthor ? 'LZ' : `${props.index + 1}楼`}</div>
            </div>
            <div class="user-info">
              <div class={['name', isAuthor || c.is_expert ? 'red' : '']}>
                {c.user_name || '匿名用户'}
                <span class="reportInfo" />
              </div>
              <div class="time"></div>
            </div>
          </div>

          <div class="comment-item-bd">
            <div class="text">
              <span
                class="clamp-body"
                style={isLong && !textExpanded.value ? 'overflow:hidden;white-space:pre-line' : 'white-space:pre-line'}
              >
                <span style="box-shadow:transparent 0px 0px">
                  <span aria-label={words}>{`\n  ${isLong && !textExpanded.value ? words.slice(0, TRUNCATE_LEN) + ' …' : words}`}</span>
                  {isLong && !textExpanded.value && (
                    <button type="button" class="clamp-away btn-reset" onClick={() => (textExpanded.value = true)}>
                      展开
                    </button>
                  )}
                </span>
              </span>
            </div>

            <div class="secs">
              <span class="time">{timeAgo(c.created_at)}</span>
              <button
                type="button"
                class="btn-hf btn-reset"
                onClick={() => c.id != null && handleReply(c.id, c.user_id ?? 0, c.user_name ?? '')}
              >
                <span>
                  {/* 原来是 label，借 `.btn-hf span label` 的小字号；换成 span 后会命中 `.btn-hf span` 的胶囊样式，这里清掉 */}
                  <span class="!inline !p-0 !bg-transparent !rounded-none !text-[0.2rem]">
                    回复
                  </span>
                </span>
              </button>
              <button
                type="button"
                class={['zan', 'post', 'btn-reset', liked ? 'act' : '']}
                onClick={() => c.id != null && props.onLike(c.id, c.user_id ?? 0)}
              >
                <VanIcon name={liked ? 'good-job' : 'good-job-o'} color="#999999" />
                {c.support ?? 0}
              </button>
            </div>

            {repliesExpanded.value && childComments.value.length > 0 && (
              <div class="reply-info-box">
                {childComments.value.map((reply) => {
                  const replyWords = reply.words ?? ''
                  return (
                    <div key={reply.id} class="reply-info">
                      <div class="reply-info-user">
                        <div class="user-img-to">
                          <div class="left-image">
                            <VanImage round src={reply.user_pic || ''} width="20px" height="20px" />
                          </div>
                          <div class="user-info van-ellipsis">
                            <span class="moren">{reply.user_name || '匿名用户'}</span>
                            <span></span>
                          </div>
                        </div>
                      </div>
                      <div class="content">
                        {reply.reply_to_user_name && (
                          <span class="user-img-to-text">
                            <i class="iplay van-icon van-icon-play" />
                            <label class="moren">{reply.reply_to_user_name}</label>
                          </span>
                        )}
                        <div style="margin-left:25px;display:flex;width:100%;margin-top:10px">
                          <span class="clamp-body" style="overflow:hidden;white-space:pre-line">
                            <span style="box-shadow:transparent 0px 0px">
                              <span aria-label={replyWords}>{`\n  ${replyWords}\n  `}</span>
                            </span>
                          </span>
                        </div>
                      </div>
                      <div class="myfot" style="margin-left:25px;margin-bottom:10px;margin-top:10px">
                        <div class="fotright">
                          <div class="etime">{timeAgo(reply.created_at)}</div>
                          <button
                            type="button"
                            class="zan pot btn-reset"
                            style="position:relative"
                            onClick={() => handleReply(c.id!, reply.user_id!, reply.user_name || '')}
                          >
                            <span class="reply-btn">回复</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {totalReplies > 0 && (
              <div class="operate">
                {!repliesExpanded.value && (
                  <span>
                    {loadingReplies.value ? (
                      <span class="m-nus">
                        <b class="minus" />加载中...
                      </span>
                    ) : (
                      <button type="button" class="m-nus btn-reset" onClick={toggleReplies}>
                        <b class="minus" />
                        {'\u00a0 展开'}{totalReplies}{'条回复 \u00a0'}
                        <VanIcon name="arrow-down" />
                      </button>
                    )}
                  </span>
                )}
                {repliesExpanded.value && (
                  <button type="button" class="btn-reset" style="margin-left:10px" onClick={toggleReplies}>
                    收起
                    <VanIcon name="arrow-up" />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )
    }
  },
})

// ==================== 评论区 ====================

export const CommentSection = defineComponent({
  props: {
    scene: { type: Number as () => SocialScene, required: true },
    targetId: { type: Number, default: null },
    lotteryType: { type: Number as () => 1 | 2, default: null },
    // 楼主 user_id（string），命中则显示「LZ」徽标，并支持「只看楼主」过滤
    authorId: { type: String, default: '' },
    // 启用「最新评论 / 只看楼主」切换
    enableOnlyAuthor: { type: Boolean, default: false },
    // 启用「正序 / 倒序」切换（默认正序 asc=true）
    enableOrderToggle: { type: Boolean, default: false },
    // 发表/回复成功后，把后端最新的评论总数透传给页面
    onCountUpdate: { type: Function as unknown as () => (count: number) => void, default: null },
    // tool-bottom props
    collectTargetId: { type: Number, default: null },
    likeCount: { type: Number, default: 0 },
    commentCount: { type: Number, default: 0 },
    collectCount: { type: Number, default: 0 },
    initialLiked: { type: Boolean, default: false },
    initialCollected: { type: Boolean, default: false },
    hideCollect: { type: Boolean, default: false },
    hideChat: { type: Boolean, default: false },
    hideToolBottom: { type: Boolean, default: false },
  },
  setup(props, { expose }) {
    const router = useRouter()
    const authStore = useAuthStore()
    const regionStore = useRegionStore()
    const hotComments = ref<Comment[]>([])
    const latestComments = ref<Comment[]>([])
    const loadingHot = ref(false)
    const loadingLatest = ref(false)
    const finished = ref(false)
    const page = ref(1)
    const PAGE_SIZE = 20

    // 最新评论 / 只看楼主 tab；正序 / 倒序 排序
    const commentTab = ref<'latest' | 'author'>('latest')
    const commentAsc = ref(true)

    // 只看楼主：客户端按 user_id 过滤（接口无该参数）
    const visibleLatest = computed<Comment[]>(() => {
      if (commentTab.value !== 'author' || !props.authorId) return latestComments.value
      return latestComments.value.filter((c) => String(c.user_id ?? '') === props.authorId)
    })

    // tool-bottom 状态（优先使用共享 sync）
    const { shared, state: socialState } = useSocialState(props, false)
    const { isLiked, isCollected, localLikeCount, localCollectCount } = socialState

    async function fetchHot() {
      loadingHot.value = true
      try {
        const res = await getApiV1CommentPopular({ query: { page: 1, size: 20 } })
        hotComments.value = res.data?.data?.list ?? []
      } catch { /* ignore */ } finally {
        loadingHot.value = false
      }
    }

    async function fetchLatest(reset = false) {
      if (!props.targetId) return
      if (reset) {
        page.value = 1
        finished.value = false
        latestComments.value = []
      }
      loadingLatest.value = true
      try {
        const res = await getApiV1CommentList({
          query: {
            type: props.scene,
            id: props.targetId,
            page: page.value,
            size: PAGE_SIZE,
            ...(props.enableOrderToggle ? { asc: commentAsc.value } : {}),
          },
        })
        const list = res.data?.data?.list ?? []
        latestComments.value = reset ? list : [...latestComments.value, ...list]
        // 后端 list 返回总数时，回传给页面
        const total = res.data?.data?.comments_count
        if (typeof total === 'number' && props.onCountUpdate) props.onCountUpdate(total)
        if (list.length < PAGE_SIZE) finished.value = true
        else page.value++
      } catch { /* ignore */ } finally {
        loadingLatest.value = false
      }
    }

    watch(commentAsc, () => {
      if (props.enableOrderToggle) fetchLatest(true)
    })

    function updateList(list: Comment[], id: number): Comment[] {
      return list.map((c) => c.id === id ? { ...c, supported: !c.supported, support: (c.support ?? 0) + (c.supported ? -1 : 1) } : c)
    }

    async function handleCommentLike(id: number, _userId: number) {
      if (!authStore.isLoggedIn) { router.push('/login'); return }
      hotComments.value = updateList(hotComments.value, id)
      latestComments.value = updateList(latestComments.value, id)
      try {
        await postApiV1CommentLike({
          body: {
            comment_id: id,
            object_id: props.targetId,
            object_type: SCENE_TO_COMMENT_LIKE_TYPE[props.scene] ?? props.scene,
          },
        })
      } catch {
        hotComments.value = updateList(hotComments.value, id)
        latestComments.value = updateList(latestComments.value, id)
      }
    }

    // tool-bottom actions
    const { handleLike, handleCollect } = useLikeCollect(props, socialState)

    // ========== 发评论弹窗（van-popup position="right"，1:1 复刻 49208 set-comment-box） ==========
    const setCommentShow = ref(false)
    const replyContext = ref<{ commentId: number; toUserId: number; toUserName: string } | null>(null)
    const content = ref('')
    const fileList = ref<UploaderFileListItem[]>([])
    const setCommentLoading = ref(false)

    async function onCommentImageUpload(item: UploaderFileListItem | UploaderFileListItem[]) {
      const items = Array.isArray(item) ? item : [item]
      for (const it of items) {
        if (!it.file) continue
        it.status = 'uploading'
        it.message = '上传中'
        try {
          it.url = await uploadImage(it.file, 'comment')
          it.status = 'done'
          it.message = ''
        } catch {
          it.status = 'failed'
          it.message = '上传失败'
        }
      }
    }

    function openCommentPopup(replyTo?: ReplyContext) {
      replyContext.value = replyTo ?? null
      content.value = ''
      fileList.value = []
      setCommentShow.value = true
    }

    expose({ openCommentPopup })

    provide(COMMENT_POPUP_KEY, { open: openCommentPopup })

    // 注册到页面级 socialSync，让同级 SocialStatsRow 的「评论」按钮也能调起本弹窗
    if (shared) {
      shared.commentPopupOpen.value = openCommentPopup
      onBeforeUnmount(() => {
        if (shared.commentPopupOpen.value === openCommentPopup) {
          shared.commentPopupOpen.value = null
        }
      })
    }

    async function submitComment() {
      if (!authStore.isLoggedIn) { router.push('/login'); return }
      if (!props.targetId) return
      const words = content.value.trim()
      if (!words) { showToast('请输入评论内容'); return }
      setCommentLoading.value = true
      try {
        const rc = replyContext.value
        const res = await postApiV1Comment({
          body: {
            id: props.targetId,
            type: props.scene,
            words,
            lottery_type: props.lotteryType ?? (regionStore.region === 'aocai' ? 2 : 1),
            ...(rc ? { comment_id: rc.commentId, reply_to_user: rc.toUserId } : {}),
          },
        })
        if (res.data?.code !== 0) {
          showToast(res.data?.message || (rc ? '回复失败，请稍后重试' : '评论失败，请稍后重试'))
          return
        }
        const newCount = (res.data?.data as { comments_count?: number } | undefined)?.comments_count
        if (typeof newCount === 'number' && props.onCountUpdate) props.onCountUpdate(newCount)
        showToast(rc ? '回复成功' : '评论成功')
        setCommentShow.value = false
        // 回复挂到的可能是热门评论的子楼，同时刷热门列表，让新回复立刻可见
        fetchLatest(true)
        fetchHot()
      } catch {
        showToast(replyContext.value ? '回复失败，请稍后重试' : '评论失败，请稍后重试')
      } finally {
        setCommentLoading.value = false
      }
    }

    function handleCommentTap() {
      if (!authStore.isLoggedIn) { router.push('/login'); return }
      if (!props.targetId) return
      openCommentPopup()
    }

    function loadAll() {
      fetchHot()
      fetchLatest(true)
    }

    watch(() => props.targetId, (val) => { if (val) loadAll() })
    watch(() => props.lotteryType, (val) => { if (val) fetchHot() })
    onMounted(() => { if (props.targetId || props.lotteryType) loadAll() })

    const LoadingSpinner = () => (
      <div class="wrapper">
        <div class="loading">
          <div class="ball first" />
          <div class="ball second" />
        </div>
      </div>
    )

    return () => (
      <div class="comment-list">
        {/* 热门评论 */}
        {hotComments.value.length > 0 && (
          <div class="hot-list">
            <div class="details-title"><span>热门评论</span></div>
            <div class="title-bk" />
            {loadingHot.value
              ? LoadingSpinner()
              : hotComments.value.map((c, i) => (
                  <CommentItem
                    key={c.id}
                    comment={c}
                    index={i}
                    scene={props.scene}
                    targetId={props.targetId}
                    authorId={props.authorId}
                    onLike={handleCommentLike}
                  />
                ))}
          </div>
        )}

        {/* 最新评论 */}
        <div class="new-list">
          <div class="jhs">
            <div class="mc-title">
              <span class={commentTab.value === 'latest' ? 'active' : ''}>
                <button type="button" class="btn-reset" onClick={() => (commentTab.value = 'latest')}>
                  最新评论
                </button>
              </span>
              {props.enableOnlyAuthor && (
                <span class={commentTab.value === 'author' ? 'active' : ''}>
                  <button type="button" class="btn-reset" onClick={() => (commentTab.value = 'author')}>
                    只看楼主
                  </button>
                </span>
              )}
            </div>
            {props.enableOrderToggle && visibleLatest.value.length > 0 && (
              <div class="mc-tab">
                <span class={commentAsc.value ? 'active' : ''}>
                  <button type="button" class="btn-block h-full" onClick={() => (commentAsc.value = true)}>
                    正序
                  </button>
                </span>
                <span class={commentAsc.value ? '' : 'active'}>
                  <button type="button" class="btn-block h-full" onClick={() => (commentAsc.value = false)}>
                    倒序
                  </button>
                </span>
              </div>
            )}
          </div>
          <div class="title-bk" />
          {!loadingLatest.value && visibleLatest.value.length === 0 && (
            <VanEmpty description="还没有评论,您的机会来了" imageSize={50} />
          )}
          <VanList
            loading={loadingLatest.value}
            onUpdate:loading={(v: boolean) => (loadingLatest.value = v)}
            finished={finished.value}
            finishedText=""
            onLoad={() => fetchLatest()}
          >
            {visibleLatest.value.map((c, i) => (
              <CommentItem
                key={c.id}
                comment={c}
                index={i}
                scene={props.scene}
                targetId={props.targetId}
                authorId={props.authorId}
                onLike={handleCommentLike}
              />
            ))}
          </VanList>
        </div>

        {/* tool-bottom（位置与 49208 一致，在 comment-list 内部） */}
        {!props.hideToolBottom && (() => {
          // 点赞、分享固定 2 个，另两个按 hideChat / hideCollect 计算（总宽 24）
          const visible = 2 + (props.hideChat ? 0 : 1) + (props.hideCollect ? 0 : 1)
          const colSpan = String(Math.floor(24 / visible))
          return (
          <div class="tool-bottom">
            <div class="tool-bottom-body">
              <VanRow>
                <VanCol span="16">
                  <button type="button" class="left btn-reset" onClick={handleCommentTap}>
                    {/* 原来是 label，样式在 `.tool-bottom-body .left label` 上，挪到类上 */}
                    <span class="block ml-[0.3rem] text-[0.24rem]">想说点什么...</span>
                  </button>
                </VanCol>
                <VanCol span="8">
                  <VanRow class="right">
                    <VanCol span={colSpan}>
                      <VanIcon
                        name={isLiked.value ? 'good-job' : 'good-job-o'}
                        color="#999999"
                        size="18"
                        class={isLiked.value ? 'act' : ''}
                        onClick={handleLike}
                      />
                      <div class="label">{formatCount(localLikeCount.value) || ''}</div>
                    </VanCol>
                    {!props.hideChat && (
                      <VanCol span={colSpan} onClick={handleCommentTap}>
                        <VanIcon name="chat-o" color="#999999" size="18" />
                        <div class="label">{formatCount(props.commentCount) || ''}</div>
                      </VanCol>
                    )}
                    {!props.hideCollect && (
                      <VanCol span={colSpan} onClick={handleCollect}>
                        <VanIcon name={isCollected.value ? 'star' : 'star-o'} size="18" class={isCollected.value ? 'act' : ''} />
                        <div class="label">{formatCount(localCollectCount.value) || ''}</div>
                      </VanCol>
                    )}
                    <VanCol span={colSpan} onClick={shareCurrentPage}>
                      <img src="/share-49.png" alt="" />
                      <div class="label">分享</div>
                    </VanCol>
                  </VanRow>
                </VanCol>
              </VanRow>
            </div>
          </div>
          )
        })()}

        {/* 发评论弹窗（1:1 复刻 49208 set-comment-box，右侧滑入） */}
        <VanPopup
          v-model:show={setCommentShow.value}
          position="right"
          overlay={false}
          style={{ height: '100%', width: '100%' }}
        >
          <div class="set-comment-box">
            <VanNavBar title="评论" onClickLeft={() => (setCommentShow.value = false)}>
              {{
                left: () => <VanIcon name="arrow-left" color="#333" size="16" />,
              }}
            </VanNavBar>
            <div class="from-box">
              <div class="title">{replyContext.value ? '回复评论' : '发布评论'}</div>
              <div class="content">
                <VanField
                  v-model={content.value}
                  rows="6"
                  autosize
                  maxlength="1200"
                  show-word-limit
                  type="textarea"
                  placeholder={replyContext.value ? `回复@${replyContext.value.toUserName}` : '请输入评论的内容'}
                />
              </div>
              <div class="uploader">
                <VanUploader v-model={fileList.value} multiple maxCount={9} afterRead={onCommentImageUpload} />
              </div>
            </div>
            <div class="ft-btn">
              <VanButton type="primary" round block loading={setCommentLoading.value} onClick={submitComment}>
                发布
              </VanButton>
            </div>
          </div>
        </VanPopup>
      </div>
    )
  },
})

// ==================== 点赞评论栏（行内统计条） ====================

export const SocialStatsRow = defineComponent({
  props: {
    scene: { type: Number as () => SocialScene, required: true },
    targetId: { type: Number, default: null },
    collectTargetId: { type: Number, default: null },
    targetUserId: { type: Number, default: 0 },
    likeCount: { type: Number, default: 0 },
    commentCount: { type: Number, default: 0 },
    collectCount: { type: Number, default: 0 },
    readCount: { type: Number, default: 0 },
    initialLiked: { type: Boolean, default: false },
    initialCollected: { type: Boolean, default: false },
  },
  setup(props) {
    const { shared, state } = useSocialState(props, true)
    const { isLiked, isCollected, localLikeCount, localCollectCount } = state
    const { handleLike, handleCollect } = useLikeCollect(props, state)
    const handleCommentTap = useCommentTap(props, shared)

    return () => (
      <div class="interaction">
        <VanRow>
          <VanCol span="18">
            <button type="button" class="tool-item btn-reset" onClick={handleLike}>
              <VanIcon name={isLiked.value ? 'good-job' : 'good-job-o'} size="16" class={isLiked.value ? 'act' : ''} />
              {formatCount(localLikeCount.value)}点赞
            </button>
            <button type="button" class="tool-item btn-reset" onClick={handleCommentTap}>
              <VanIcon name="chat" size="16" />
              {formatCount(props.commentCount)}评论
            </button>
            <button type="button" class="tool-item btn-reset" onClick={handleCollect}>
              <VanIcon name={isCollected.value ? 'star' : 'star-o'} size="16" class={isCollected.value ? 'act' : ''} />
              {formatCount(localCollectCount.value)}收藏
            </button>
          </VanCol>
          <VanCol span="6">
            {!!props.readCount && (
              <div class="right-text">{formatCount(props.readCount)}次阅读</div>
            )}
          </VanCol>
        </VanRow>
      </div>
    )
  },
})

// ==================== 底部互动栏（保留给其他页面使用）====================

export const SocialBottomBar = defineComponent({
  props: {
    scene: { type: Number as () => SocialScene, required: true },
    targetId: { type: Number, default: null },
    collectTargetId: { type: Number, default: null },
    targetUserId: { type: Number, default: 0 },
    likeCount: { type: Number, default: 0 },
    collectCount: { type: Number, default: 0 },
    commentCount: { type: Number, default: 0 },
    initialLiked: { type: Boolean, default: false },
    initialCollected: { type: Boolean, default: false },
    hideCollect: { type: Boolean, default: false },
  },
  setup(props) {
    const { shared, state } = useSocialState(props, true)
    const { isLiked, isCollected, localLikeCount, localCollectCount } = state
    const { handleLike, handleCollect } = useLikeCollect(props, state)
    const handleCommentTap = useCommentTap(props, shared)

    return () => (
      <div class="tool-bottom">
        <div class="tool-bottom-body">
          <VanRow>
            <VanCol span="16">
              <button type="button" class="left btn-reset" onClick={handleCommentTap}>
                <span class="block ml-[0.3rem] text-[0.24rem]">想说点什么...</span>
              </button>
            </VanCol>
            <VanCol span="8">
              <VanRow class="right">
                <VanCol span="8">
                  <VanIcon
                    name={isLiked.value ? 'good-job' : 'good-job-o'}
                    color="#999999"
                    size="18"
                    class={isLiked.value ? 'act' : ''}
                    onClick={handleLike}
                  />
                  <div class="label">{formatCount(localLikeCount.value) || ''}</div>
                </VanCol>
                {!props.hideCollect && (
                  <VanCol span="8" onClick={handleCollect}>
                    <VanIcon name={isCollected.value ? 'star' : 'star-o'} size="18" class={isCollected.value ? 'act' : ''} />
                    <div class="label">{formatCount(localCollectCount.value) || ''}</div>
                  </VanCol>
                )}
                <VanCol span="8" onClick={shareCurrentPage}>
                  <img src="/share-49.png" alt="" />
                  <div class="label">分享</div>
                </VanCol>
              </VanRow>
            </VanCol>
          </VanRow>
        </div>
      </div>
    )
  },
})
